import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FiInbox } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

/* ── shimmer ── */
const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const Bone = styled.div`
  background: linear-gradient(90deg, #1e1e1e 25%, #2a2a2a 50%, #1e1e1e 75%);
  background-size: 1200px 100%;
  animation: ${shimmer} 1.6s infinite linear;
  border-radius: ${p => p.r || '4px'};
`;

/* ─────────────────────────────────────────
   SERVER / VIDEO / SHOP  card skeleton
───────────────────────────────────────── */
const SCardWrap = styled.div`
  background: #1A1A1A;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.04);
`;

export const SkeletonCard = () => (
  <SCardWrap>
    <Bone style={{ width: '100%', aspectRatio: '16/9' }} r="0" />
    <div style={{ padding: '10px 12px 14px' }}>
      <Bone style={{ height: 14, width: '68%', marginBottom: 8 }} />
      <Bone style={{ height: 10, width: '50%', marginBottom: 6 }} />
      <Bone style={{ height: 10, width: '36%', marginBottom: 12 }} />
      <Bone style={{ height: 28, width: '55%', margin: '0 auto', borderRadius: 6 }} r="6px" />
    </div>
  </SCardWrap>
);

/* ─────────────────────────────────────────
   CONTENT CREATOR  card skeleton
───────────────────────────────────────── */
const CCardWrap = styled.div`
  background: #1A1A1A;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.04);
`;

export const SkeletonCreatorCard = () => (
  <CCardWrap>
    <Bone style={{ height: 76 }} r="0" />
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: -24, padding: '0 14px 18px' }}>
      <Bone style={{ width: 52, height: 52, borderRadius: '50%', marginBottom: 10, border: '3px solid #111' }} r="50%" />
      <Bone style={{ height: 13, width: '60%', marginBottom: 6 }} />
      <Bone style={{ height: 10, width: '38%', marginBottom: 14 }} />
      <div style={{ display: 'flex', gap: 8, width: '100%', marginBottom: 10 }}>
        {[0, 1, 2].map(i => <Bone key={i} style={{ flex: 1, height: 38, borderRadius: 8 }} r="8px" />)}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {[0, 1].map(i => <Bone key={i} style={{ height: 28, width: 70, borderRadius: 6 }} r="6px" />)}
      </div>
    </div>
  </CCardWrap>
);

/* ─────────────────────────────────────────
   TOPLIST  row skeleton
───────────────────────────────────────── */
const LRowWrap = styled.div`
  background: #1A1A1A;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.04);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const SkeletonListRow = () => (
  <LRowWrap>
    <Bone style={{ width: 34, height: 34, flexShrink: 0 }} r="50%" />
    <div style={{ flex: 1, minWidth: 0 }}>
      <Bone style={{ height: 13, width: '45%', marginBottom: 7 }} />
      <Bone style={{ height: 10, width: '28%' }} />
    </div>
    <Bone style={{ width: 80, height: 48, flexShrink: 0 }} r="6px" />
    <Bone style={{ width: 58, height: 36, flexShrink: 0 }} r="8px" />
  </LRowWrap>
);

/* ─────────────────────────────────────────
   SERVICE  card skeleton
───────────────────────────────────────── */
const SVCardWrap = styled.div`
  background: #1A1A1A;
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.04);
  padding: 22px 18px;
`;

export const SkeletonServiceCard = () => (
  <SVCardWrap>
    <Bone style={{ width: 48, height: 48, marginBottom: 14 }} r="12px" />
    <Bone style={{ height: 15, width: '58%', marginBottom: 10 }} />
    <Bone style={{ height: 10, width: '92%', marginBottom: 5 }} />
    <Bone style={{ height: 10, width: '78%', marginBottom: 5 }} />
    <Bone style={{ height: 10, width: '52%', marginBottom: 16 }} />
    <div style={{ display: 'flex', gap: 6 }}>
      <Bone style={{ height: 22, width: 56 }} r="20px" />
      <Bone style={{ height: 22, width: 56 }} r="20px" />
    </div>
  </SVCardWrap>
);

/* ─────────────────────────────────────────
   NO DATA  state
───────────────────────────────────────── */
const NDWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 20px;
  text-align: center;

  .nd-icon  { font-size: 56px; color: #374151; margin-bottom: 18px; }
  .nd-title { font-size: 15px; font-weight: 600; color: #6b7280; margin-bottom: 6px; }
  .nd-sub   { font-size: 12.5px; color: #4b5563; }
`;

export const NoData = ({ title, sub }) => {
  const { t } = useTranslation();
  return (
    <NDWrap>
      <FiInbox className="nd-icon" />
      <div className="nd-title">{title ?? t('general.no_data')}</div>
      {sub && <div className="nd-sub">{sub}</div>}
    </NDWrap>
  );
};
